"use client";

import { Alignment, Fit, Layout, useRive } from "@rive-app/react-webgl2";
import type { Rive } from "@rive-app/react-webgl2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

import {
  RiveSafetyBoundary,
  useRiveCleanup,
  useRiveLoadFailure,
  useRiveWebGLContextGuard,
} from "@/components/rive/rive-safety-boundary";

const RIVE_SRC = "/assets-next/join-us-planet/join-us-planet.riv";
const ARTBOARD = "Join us-Desktop";
const STATE_MACHINE = "State Machine 1";
const VIEW_MODEL = "SpaceVM";
const VIEW_MODEL_INSTANCE = "Instance";

/** SpaceVM string properties → Figma location labels (5650-21141). */
const SPACE_VM_LOCATIONS: Record<string, string> = {
  dublin: "Dublin",
  london: "London",
  charlotte: "Charlotte",
  newYork: "New York",
  sydney: "Sydney",
  remote: "Remote",
  remote2: "Remote",
  remote3: "Remote",
  // The .riv slot formerly labelled Barcelona now marks the Berlin office.
  barcelona: "Berlin",
  // Unused in Figma desktop/mobile — clear defaults baked into the .riv file.
  madrid: "",
};

function applySpaceVmLocations(rive: Rive) {
  const viewModel = rive.viewModelByName(VIEW_MODEL);
  if (!viewModel) return;

  const instance =
    viewModel.instanceByName(VIEW_MODEL_INSTANCE) ??
    viewModel.defaultInstance() ??
    viewModel.instance();

  for (const [path, label] of Object.entries(SPACE_VM_LOCATIONS)) {
    // Each location is a nested view model exposing a `planetName` string.
    const property = instance.viewModel(path)?.string("planetName");
    if (property) property.value = label;
  }

  rive.bindViewModelInstance(instance);
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

/** Figma container width → height anchors (5626-20554 mobile, 5626-20472 lg, 5628-20781 xl). */
const BANNER_HEIGHT_ANCHORS = [
  [335, 522],
  [704, 690],
  [1376, 710],
] as const;

function computeBannerHeight(width: number): number {
  const minWidth = BANNER_HEIGHT_ANCHORS[0][0];
  const minHeight = BANNER_HEIGHT_ANCHORS[0][1];

  if (width <= minWidth) {
    return Math.round(width * (minHeight / minWidth));
  }

  let prevWidth: number = BANNER_HEIGHT_ANCHORS[0][0];
  let prevHeight: number = BANNER_HEIGHT_ANCHORS[0][1];

  for (let i = 1; i < BANNER_HEIGHT_ANCHORS.length; i++) {
    const anchor = BANNER_HEIGHT_ANCHORS[i];
    if (!anchor) continue;
    const [nextWidth, nextHeight] = anchor;
    if (width <= nextWidth) {
      const progress = (width - prevWidth) / (nextWidth - prevWidth);
      return Math.round(prevHeight + progress * (nextHeight - prevHeight));
    }
    prevWidth = nextWidth;
    prevHeight = nextHeight;
  }

  return prevHeight;
}

/** Figma panel fill sampled from join-us-planet artboard edge (~rgb(41,40,91)). */
const JOIN_US_PANEL_BG_CLASS = "bg-[#29285b]";

/**
 * Footer-banner shell: rounded clip panel + inner fill + absolute canvas.
 * Inner fill matches planet purple (not footer cyan) so corner arcs stay on-brand
 * when Cover crops the artboard rim — same layering as footer-banner-rive.
 */
const RIVE_PANEL_CLASS = "rounded-ds-xl relative w-full overflow-hidden";
const RIVE_INNER_CLASS = `absolute inset-0 ${JOIN_US_PANEL_BG_CLASS}`;
const RIVE_COMPONENT_BASE_CLASS =
  "absolute inset-0 size-full [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:size-full [&_canvas]:rounded-ds-xl";

function useBannerHeight() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(BANNER_HEIGHT_ANCHORS[0][1]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateHeight = () => {
      setHeight(computeBannerHeight(element.clientWidth));
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { containerRef, height };
}

export function JoinUsPlannetBannerRive() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { containerRef, height } = useBannerHeight();

  const fallback = (
    <JoinUsPlannetBannerFallback containerRef={containerRef} height={height} />
  );

  return (
    <RiveSafetyBoundary fallback={fallback}>
      <JoinUsPlannetBannerRiveCanvas
        prefersReducedMotion={prefersReducedMotion}
        containerRef={containerRef}
        height={height}
      />
    </RiveSafetyBoundary>
  );
}

interface JoinUsPlannetBannerRiveCanvasProps {
  prefersReducedMotion: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  height: number;
}

function JoinUsPlannetBannerRiveCanvas({
  prefersReducedMotion,
  containerRef,
  height,
}: JoinUsPlannetBannerRiveCanvasProps) {
  const { loadFailed, handleLoadError, handleContextLost } =
    useRiveLoadFailure();
  const alignment = Alignment.Center;
  // Zoom crops the artboard light-sky rim so purple shows at rounded corner arcs.
  const layoutScaleFactor = 1.12;

  const layout = useMemo(
    () =>
      new Layout({
        fit: Fit.Cover,
        alignment,
        layoutScaleFactor,
      }),
    [alignment, layoutScaleFactor]
  );

  const onRiveReady = useCallback((rive: Rive) => {
    applySpaceVmLocations(rive);
  }, []);

  const { RiveComponent, rive, canvas } = useRive(
    {
      src: RIVE_SRC,
      artboard: ARTBOARD,
      stateMachines: STATE_MACHINE,
      autoplay: !prefersReducedMotion,
      layout,
      onRiveReady,
      onLoadError: handleLoadError,
    },
    { shouldResizeCanvasToContainer: true }
  );

  useRiveWebGLContextGuard(canvas, rive, handleContextLost);
  useRiveCleanup(rive);

  useEffect(() => {
    if (rive) applySpaceVmLocations(rive);
  }, [rive]);

  useEffect(() => {
    if (!rive) return;
    rive.layout = new Layout({
      fit: Fit.Cover,
      alignment,
      layoutScaleFactor,
    });
  }, [rive, alignment, layoutScaleFactor]);

  if (loadFailed) {
    return (
      <JoinUsPlannetBannerFallback
        containerRef={containerRef}
        height={height}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={RIVE_PANEL_CLASS}
      data-testid="join-us-plannet-banner-rive"
      style={{ height }}
    >
      <div className={RIVE_INNER_CLASS}>
        <RiveComponent
          className={`${RIVE_COMPONENT_BASE_CLASS} scale-[1.12]`}
        />
      </div>
    </div>
  );
}

function JoinUsPlannetBannerFallback({
  containerRef,
  height,
}: Pick<JoinUsPlannetBannerRiveCanvasProps, "containerRef" | "height">) {
  return (
    <div
      ref={containerRef}
      aria-hidden
      className={RIVE_PANEL_CLASS}
      data-testid="join-us-plannet-banner-rive"
      style={{ height }}
    >
      <div className={RIVE_INNER_CLASS}>
        <div
          data-testid="join-us-plannet-banner-rive-fallback"
          className="absolute inset-0"
        />
      </div>
    </div>
  );
}
